// Utils
import { normalizeLocale, normalizeSystem } from "./format";

const FALLBACK_SYSTEM = "metric";
const FALLBACK_LOCALE = "en-US";
const FALLBACK_CURRENCY = "EUR";

export const SUPPORTED_CURRENCIES = new Set([
  "EUR",
  "USD",
  "GBP",
  "JPY",
  "DKK",
  "CAD",
  "AUD",
  "CHF"
]);

/** Payload symbols that stand in for currency but are not ISO codes. */
export const CURRENCY_PLACEHOLDER_SYMBOLS = new Set(["cur"]);

/*
A single source of truth for resolving unit preferences from a user profile.
*/
export function resolveUnitPreferences(profile = {}) {
  const system = normalizeSystem(profile?.unit_system || FALLBACK_SYSTEM);
  const locale = normalizeLocale(profile?.number_locale || FALLBACK_LOCALE);

  return { system, locale };
}

export function resolveDisplayCurrency(profile = {}) {
  const raw = profile?.display_currency;
  if (typeof raw !== "string") return FALLBACK_CURRENCY;

  const normalized = raw.trim().toUpperCase();
  return SUPPORTED_CURRENCIES.has(normalized) ? normalized : FALLBACK_CURRENCY;
}

/**
 * Localized currency symbol for column headers and labels (e.g. ¥, $, €).
 * Display-only: no FX conversion. Falls back to the ISO code when Intl cannot
 * resolve a distinct symbol for the given locale.
 *
 * @param {string} currencyCode
 * @param {string} [locale]
 * @returns {string}
 */
export function resolveCurrencyDisplaySymbol(
  currencyCode,
  locale = FALLBACK_LOCALE
) {
  const code = resolveDisplayCurrency({ display_currency: currencyCode });
  const resolvedLocale = normalizeLocale(locale);

  try {
    const parts = new Intl.NumberFormat(resolvedLocale, {
      style: "currency",
      currency: code
    }).formatToParts(0);
    const currencyPart = parts.find(part => part.type === "currency");
    return currencyPart?.value || code;
  } catch {
    return code;
  }
}

/**
 * Returns true when a unit symbol is empty or a known currency placeholder.
 *
 * @param {string} symbol
 * @returns {boolean}
 */
export function isCurrencyPlaceholder(symbol) {
  if (symbol == null) return true;

  const normalized = String(symbol).trim().toLowerCase();
  return normalized === "" || CURRENCY_PLACEHOLDER_SYMBOLS.has(normalized);
}

/** Common currency glyphs that may appear in API column labels, e.g. "Cost (€)". */
const COMMON_CURRENCY_GLYPHS = new Set(["€", "$", "£", "¥", "₹"]);
const LENGTH_UNIT_TOKENS = new Set(["km", "m", "mi", "ft"]);

function isCurrencyLikeLabelToken(token) {
  const trimmed = String(token).trim();
  if (!trimmed) return false;
  if (isCurrencyPlaceholder(trimmed)) return true;
  if (SUPPORTED_CURRENCIES.has(trimmed.toUpperCase())) return true;
  if (COMMON_CURRENCY_GLYPHS.has(trimmed)) return true;
  return false;
}

function isCurrencyPerLengthLikeLabelToken(token) {
  const trimmed = String(token).trim();
  if (!trimmed) return false;
  if (isCurrencyLikeLabelToken(trimmed)) return true;

  const [currencyToken, lengthToken, ...rest] = trimmed
    .split("/")
    .map(part => part.trim());
  if (rest.length > 0 || !currencyToken || !lengthToken) return false;

  return (
    isCurrencyLikeLabelToken(currencyToken) &&
    LENGTH_UNIT_TOKENS.has(lengthToken.toLowerCase())
  );
}

/**
 * Rewrites parenthetical currency tokens in KPI column labels to the user's
 * display symbol. Display-only: amounts are unchanged.
 *
 * @example substituteCurrencyInLabel("Cost (€)", "JPY", "en-US") => "Cost (¥)"
 *
 * @param {string} label
 * @param {string} userCurrency
 * @param {string} [locale]
 * @returns {string}
 */
export function substituteCurrencyInLabel(
  label,
  userCurrency,
  locale = FALLBACK_LOCALE
) {
  if (typeof label !== "string" || !label) return label;

  const symbol = resolveCurrencyDisplaySymbol(userCurrency, locale);
  return label.replace(/\(([^)]+)\)/g, (match, inner) =>
    isCurrencyPerLengthLikeLabelToken(inner)
      ? `(${symbol}${inner.includes("/") ? `/${inner.split("/")[1].trim()}` : ""})`
      : match
  );
}

/**
 * Resolves the ISO currency code used for display. User currency is always
 * authoritative; payload symbols (including placeholders like cur) are ignored.
 *
 * Display-only: numeric values are never converted — only the label/symbol shown
 * in cost views (Costs Explorer, path details, etc.) changes.
 *
 * @param {string|Object} _payloadUnits
 * @param {string} userCurrency
 * @returns {string}
 */
export function resolveEffectiveCurrency(_payloadUnits, userCurrency) {
  return resolveDisplayCurrency({ display_currency: userCurrency });
}
